import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { INDUSTRY_TYPES } from "@/constants/industries";

const API = process.env.REACT_APP_BACKEND_URL;

function getImgSrc(imageUrl) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl; // external URL — use as-is
  return `${API}${imageUrl}`;                        // local upload — prepend backend
}

function getInitials(name = "") {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "AD"
  );
}

function getIndustryInfo(advertiserType) {
  return (
    INDUSTRY_TYPES.find((i) => i.value === advertiserType) || {
      label: advertiserType?.replace(/_/g, " ") || "Business",
      color: "#2ECC71",
      icon: null,
    }
  );
}

export default function RotatingBanners({ banners = [], onCTAClick, compact = false, light = false, trackImpression = false }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const trackedIds = useRef(new Set());

  useEffect(() => {
    setIdx(0);
  }, [banners]);

  // Track impression once per banner per session mount
  useEffect(() => {
    if (!trackImpression || !banners.length) return;
    const banner = banners[idx];
    if (banner?.id && !trackedIds.current.has(banner.id)) {
      trackedIds.current.add(banner.id);
      fetch(`${API}/api/marketplace/banners/${banner.id}/impression`, { method: "POST" }).catch(() => {});
    }
  }, [idx, trackImpression, banners]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % banners.length);
        setVisible(true);
      }, 280);
    }, 8000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (!banners.length) return null;

  const b = banners[idx];
  const industry = getIndustryInfo(b.advertiser_type);
  const IconComp = industry.icon;
  const imgSrc = getImgSrc(b.image_url);
  const initials = getInitials(b.business_name || b.title);

  return (
    <div data-testid="rotating-banners" className="w-full">
      {/* Ad Card */}
      <div
        className="relative overflow-hidden rounded-2xl border transition-all duration-300"
        style={{
          background: light
            ? "white"
            : "linear-gradient(160deg, #111827 0%, #0f172a 100%)",
          borderColor: light ? "#e2e8f0" : `${industry.color}35`,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0px)" : "translateY(5px)",
          boxShadow: light
            ? "0 1px 8px rgba(0,0,0,0.06)"
            : `0 0 40px ${industry.color}10`,
        }}
      >
        {/* Top accent stripe */}
        <div
          className="absolute top-0 left-0 right-0 h-0.5 z-10"
          style={{ background: `linear-gradient(90deg, ${industry.color}, transparent)` }}
        />

        {/* Image area */}
        {imgSrc ? (
          <div className="relative overflow-hidden" style={{ maxHeight: compact ? 140 : 200 }}>
            <img
              src={imgSrc}
              alt={b.title || b.business_name}
              className="w-full object-cover"
              style={{ maxHeight: compact ? 140 : 200 }}
              onError={(e) => { e.target.parentElement.style.display = "none"; }}
            />
            {/* Gradient overlay on image for text legibility */}
            <div className={`absolute inset-0 bg-gradient-to-t ${light ? "from-white/80" : "from-[#0f172a]"} via-transparent to-transparent`} />
          </div>
        ) : (
          /* Professional no-image fallback */
          <div
            className="relative flex items-center justify-center overflow-hidden"
            style={{
              height: compact ? 100 : 130,
              background: `linear-gradient(135deg, ${industry.color}15 0%, #0f172a 70%)`,
            }}
          >
            {/* Decorative background circles */}
            <div
              className="absolute -right-10 -top-10 w-44 h-44 rounded-full opacity-10"
              style={{ background: industry.color }}
            />
            <div
              className="absolute -left-6 -bottom-6 w-28 h-28 rounded-full opacity-8"
              style={{ background: industry.color }}
            />
            {/* Business initials circle */}
            <div
              className="relative z-10 flex items-center justify-center rounded-2xl font-black text-white shadow-2xl"
              style={{
                width: compact ? 60 : 76,
                height: compact ? 60 : 76,
                fontSize: compact ? "1.5rem" : "1.75rem",
                background: `linear-gradient(135deg, ${industry.color}cc, ${industry.color}77)`,
                boxShadow: `0 8px 32px ${industry.color}40`,
              }}
            >
              {initials}
            </div>
            {/* Industry icon watermark */}
            {IconComp && (
              <div className="absolute right-5 bottom-3 opacity-10">
                <IconComp size={compact ? 28 : 40} style={{ color: industry.color }} />
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className={compact ? "px-4 py-3" : "px-5 py-4"}>
          {/* Meta row */}
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
              style={{ background: `${industry.color}20`, color: industry.color }}
            >
              {IconComp && <IconComp size={10} />}
              {industry.label}
            </span>
            <div className="flex items-center gap-1 text-slate-500 text-xs">
              <ShieldCheck size={11} className="text-[#2ECC71]" />
              <span>Verified · ZIP {b.zip_code}</span>
            </div>
          </div>

          {/* Business name */}
          <h3
            className="font-black leading-tight mb-1"
            style={{
              fontSize: compact ? "1rem" : "1.2rem",
              color: light ? "#0f172a" : "white",
            }}
          >
            {b.title || b.business_name}
          </h3>

          {/* Tagline */}
          {b.tagline && (
            <p className={`text-sm leading-relaxed mb-3 ${light ? "text-slate-500" : "text-slate-400"}`}>{b.tagline}</p>
          )}

          {/* CTA Button */}
          {b.cta_url && (
            <button
              onClick={() => onCTAClick?.(b)}
              className="inline-flex items-center gap-2 font-bold rounded-xl text-white transition-all hover:gap-3"
              style={{
                background: `linear-gradient(135deg, ${industry.color}, ${industry.color}99)`,
                padding: compact ? "0.4rem 1rem" : "0.55rem 1.25rem",
                fontSize: compact ? "0.8rem" : "0.875rem",
                boxShadow: `0 4px 16px ${industry.color}30`,
              }}
              data-testid={`banner-cta-${b.id}`}
            >
              {b.cta_text || "Learn More"} <ArrowRight size={compact ? 12 : 14} />
            </button>
          )}
        </div>
      </div>

      {/* Dot navigation */}
      {banners.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === idx ? "2rem" : "0.5rem",
                background: i === idx ? industry.color : (light ? "#cbd5e1" : "#334155"),
              }}
              aria-label={`Ad ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
